'use client';

import { useState, useRef, useCallback, useEffect } from 'react';

import { AppImageClient, AppImageData } from '@/shared/components/appImage';
import { classNames } from '@/shared/lib/classNames/classNames';
import { cn } from '@/shared/lib/utils/cn';

interface ImageComparisonProps {
  /** Готовый signed URL. Legacy — для новых страниц используйте beforeImageData. */
  beforeImage?: string;
  afterImage?: string;
  /** Данные AppImage (srcset + blur) из `getAppImageData`. Приоритетнее beforeImage. */
  beforeImageData?: AppImageData;
  afterImageData?: AppImageData;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
  initialPosition?: number;
  /** Отношение ширины к высоте. По умолчанию квадрат. */
  aspectRatio?: number;
}

export const ImageComparison: React.FC<ImageComparisonProps> = ({
  beforeImage,
  afterImage,
  beforeImageData,
  afterImageData,
  beforeLabel = '',
  afterLabel = '',
  className,
  initialPosition = 50,
  aspectRatio = 1,
}) => {
  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSliderPosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      updateSliderPosition(e.clientX);
    },
    [isDragging, updateSliderPosition],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      updateSliderPosition(touch.clientX);
    },
    [isDragging, updateSliderPosition],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      updateSliderPosition(e.clientX);
    },
    [updateSliderPosition],
  );

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth();

    // ResizeObserver for precise tracking of container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateContainerWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateContainerWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  const safeAspectRatio = aspectRatio > 0 ? aspectRatio : 1;
  const imageHeight = containerWidth / safeAspectRatio;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full cursor-pointer overflow-hidden select-none',
        className,
      )}
      style={{ aspectRatio: safeAspectRatio }}
      onClick={handleContainerClick}
    >
      {containerWidth > 0 && (
        <>
          {/* Before Image (Background) */}
          <div className="absolute inset-0">
            {beforeImageData ? (
              <AppImageClient
                {...beforeImageData}
                alt={beforeLabel}
                width={containerWidth}
                height={imageHeight}
                style={{ width: containerWidth, height: '100%' }}
                className="pointer-events-none h-full max-w-none object-cover object-center select-none"
              />
            ) : (
              <img
                src={beforeImage}
                alt={beforeLabel}
                width={containerWidth}
                height={imageHeight}
                style={{ width: containerWidth, height: '100%' }}
                className="pointer-events-none h-full max-w-none object-cover object-center select-none"
                draggable={false}
              />
            )}
            {/* Before Label */}
            <div className="absolute bottom-4 left-1/3 text-xl text-white">
              {beforeLabel}
            </div>
          </div>

          {/* After Image (Overlay) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${sliderPosition}%` }}
          >
            {afterImageData ? (
              <AppImageClient
                {...afterImageData}
                alt={afterLabel}
                width={containerWidth}
                height={imageHeight}
                style={{ width: containerWidth, height: '100%' }}
                className="center pointer-events-none h-full max-h-none max-w-none object-cover select-none"
              />
            ) : (
              <img
                src={afterImage}
                alt={afterLabel}
                width={containerWidth}
                height={imageHeight}
                style={{ width: containerWidth, height: '100%' }}
                className="center pointer-events-none h-full max-h-none max-w-none object-cover select-none"
                draggable={false}
              />
            )}
            {/* After Label */}
            <div className="absolute right-4 bottom-4 text-xl text-white">
              {afterLabel}
            </div>
          </div>
        </>
      )}

      {/* Slider Line */}
      <div
        className="absolute top-0 bottom-0 z-10 w-[7px] bg-white shadow-lg"
        style={{ left: `calc(${sliderPosition}% - 3px)` }}
      />

      {/* Slider Handle */}
      <div
        className={classNames({
          cls: 'rounded-20 absolute top-1/2 z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform cursor-grab items-center justify-center bg-white shadow-lg',
          mods: {
            'cursor-grabbing': isDragging,
          },
        })}
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="grid grid-cols-2 gap-0.5">
          <div className="rounded-20 bg-surface-base-inverse-primary h-1 w-1" />
          <div className="rounded-20 bg-surface-base-inverse-primary h-1 w-1" />
          <div className="rounded-20 bg-surface-base-inverse-primary h-1 w-1" />
          <div className="rounded-20 bg-surface-base-inverse-primary h-1 w-1" />
        </div>
      </div>
    </div>
  );
};
